import React, { useState } from 'react';
import type { BriefingItem } from '@/services/strategistService';
import {
  ClockIcon,
  SearchVolumeIcon,
  CopyIcon,
  EditorArrowIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckIcon,
  LightningIcon,
  ShieldCheckIcon,
  LiquidityIcon,
} from './SvgIcons';

interface ExecutiveBriefingCardProps {
  item: BriefingItem;
  onToggle: (itemId: string, currentStatus: boolean) => void;
  onDispatch: (itemId: string) => void;
}

export const ExecutiveBriefingCard: React.FC<ExecutiveBriefingCardProps> = ({
  item,
  onToggle,
  onDispatch,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    // F-411 (familia): o codemod de i18n tinha colado um useTranslation()
    // DENTRO desta funcao — hook fora do ciclo de render, "Invalid hook
    // call" a cada chamada. Era por isso que toasts/acoes daqui morriam em
    // silencio. O `t` nem era usado.
    const text = `PAUTA: ${item.titulo}
HORIZONTE: ${item.horizonte}
CATEGORIA: ${item.categoria}
FONTE: ${item.fonte_primaria}
POR QUE HOJE: ${item.por_que_hoje}

H2S OBRIGATÓRIOS:
${(item.h2s_obrigatorios ?? []).map((h, i) => `${i + 1}. ${h}`).join('\n')}

PAA HISTÓRICO (GOOGLE):
${(item.paa_historico ?? []).map((q) => `• ${q}`).join('\n')}

RESUMO 3-BULLETS PARA LEAD:
${(item.resumo_tldr_3_bullets ?? []).map((b) => `• ${b}`).join('\n')}

SCHEMA RECOMENDADO: ${item.schema_tipo}
MONETIZAÇÃO: ${item.monetizacao}`;

    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getHorizonBadgeColor = (h: string) => {
    if (h.includes('D-0')) return 'var(--sig-crit, #EF4444)';
    if (h.includes('D-60') || h.includes('D-15')) return 'var(--nexus-brand, #3B82F6)';
    return 'var(--sig-ok, #10B981)';
  };

  const getHorizonBadgeBg = (h: string) => {
    if (h.includes('D-0')) return 'rgba(239, 68, 68, 0.12)';
    if (h.includes('D-60') || h.includes('D-15')) return 'rgba(59, 130, 246, 0.12)';
    return 'rgba(16, 185, 129, 0.12)';
  };

  return (
    <div
      style={{
        backgroundColor: 'var(--bg-1)',
        border: '1px solid var(--border-1)',
        borderRadius: '8px',
        padding: '16px',
        opacity: item.concluido ? 0.75 : 1,
        transition: 'all 0.2s ease',
        boxShadow: 'var(--shadow-1, 0 1px 3px rgba(0,0,0,0.1))',
      }}
    >
      {/* NÍVEL 1: HEADER COMPACTO DO CARD */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '12px',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Fita de Badges */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              flexWrap: 'wrap',
              marginBottom: '6px',
            }}
          >
            <span
              style={{
                fontSize: '11px',
                fontWeight: 700,
                fontFamily: 'monospace',
                letterSpacing: '0.04em',
                padding: '2px 6px',
                borderRadius: '4px',
                color: getHorizonBadgeColor(item.horizonte ?? ''),
                backgroundColor: getHorizonBadgeBg(item.horizonte ?? ''),
                border: `1px solid ${getHorizonBadgeColor(item.horizonte ?? '')}`,
              }}
            >
              [{item.horizonte ?? item.categoria}]
            </span>

            <span
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: 'var(--text-3)',
                backgroundColor: 'var(--bg-2)',
                padding: '2px 6px',
                borderRadius: '4px',
                border: '1px solid var(--border-2)',
              }}
            >
              {item.categoria.toUpperCase()}
            </span>

            <span
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: 'var(--text-3)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <ShieldCheckIcon size={12} />
              {item.fonte_primaria.split(',')[0]}
            </span>
          </div>

          {/* Título da Pauta */}
          <h3
            style={{
              margin: '0 0 6px 0',
              fontSize: '15px',
              fontWeight: 700,
              color: item.concluido ? 'var(--text-3)' : 'var(--text-1)',
              textDecoration: item.concluido ? 'line-through' : 'none',
              lineHeight: 1.35,
            }}
          >
            {item.titulo}
          </h3>

          {/* Racional Algorítmico (Por que hoje?) */}
          <div
            style={{
              backgroundColor: 'var(--bg-2)',
              borderLeft: '3px solid var(--nexus-brand)',
              padding: '6px 10px',
              borderRadius: '0 4px 4px 0',
              fontSize: '12px',
              color: 'var(--text-2)',
              lineHeight: 1.4,
              marginBottom: '8px',
            }}
          >
            <strong style={{ color: 'var(--text-1)' }}>Por que hoje:</strong> {item.por_que_hoje}
          </div>
        </div>

        {/* Botão de Checkbox / Status */}
        <button
          onClick={() => onToggle(item.id, !item.concluido)}
          title={item.concluido ? 'Marcar como Pendente' : 'Marcar como Concluída'}
          style={{
            background: item.concluido ? 'var(--sig-ok-bg, rgba(16,185,129,0.15))' : 'var(--bg-2)',
            border: `1px solid ${item.concluido ? 'var(--sig-ok, #10B981)' : 'var(--border-2)'}`,
            color: item.concluido ? 'var(--sig-ok, #10B981)' : 'var(--text-3)',
            borderRadius: '6px',
            padding: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <CheckIcon size={16} />
        </button>
      </div>

      {/* RODAPÉ DO MODO COMPACTO: METADADOS & AÇÕES */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px',
          paddingTop: '8px',
          borderTop: '1px solid var(--border-1)',
          marginTop: '4px',
        }}
      >
        {/* Chips de Informação */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flexWrap: 'wrap',
            fontSize: '11px',
            color: 'var(--text-3)',
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
            <ClockIcon size={12} />
            {item.tempo_leitura}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
            <SearchVolumeIcon size={12} />
            {item.volume_busca_estimado}
          </span>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px',
              color: 'var(--sig-ok, #10B981)',
            }}
          >
            <LiquidityIcon size={12} />
            {(item.monetizacao ?? '').split('-')[0]}
          </span>
        </div>

        {/* Botões de Ação */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {/* Botão de Expansão (Briefing / H2s) */}
          <button
            onClick={() => setExpanded(!expanded)}
            style={{
              background: 'var(--bg-2)',
              border: '1px solid var(--border-2)',
              color: 'var(--text-2)',
              fontSize: '11px',
              fontWeight: 600,
              padding: '4px 8px',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            {expanded ? <ChevronUpIcon size={12} /> : <ChevronDownIcon size={12} />}
            {expanded ? 'Ocultar Detalhes' : 'Ver Briefing'}
          </button>

          {/* Botão de Despacho 1-Click */}
          <button
            onClick={() => onDispatch(item.id)}
            disabled={item.em_producao}
            style={{
              background: item.em_producao ? 'var(--bg-2)' : 'var(--nexus-brand)',
              border: 'none',
              color: item.em_producao ? 'var(--text-3)' : 'var(--text-on-accent, #ffffff)',
              fontSize: '11px',
              fontWeight: 700,
              padding: '4px 10px',
              borderRadius: '4px',
              cursor: item.em_producao ? 'default' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <LightningIcon size={12} />
            {item.em_producao ? 'Em Produção' : 'Despachar p/ IA'}
          </button>
        </div>
      </div>

      {/* NÍVEL 2: GAVETA EXPANDIDA (PROGRESSIVE DISCLOSURE) */}
      {expanded && (
        <div
          style={{
            marginTop: '12px',
            paddingTop: '12px',
            borderTop: '1px dashed var(--border-2)',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          {/* Box 1: H2s Obrigatórios & Formato */}
          <div style={{ backgroundColor: 'var(--bg-2)', padding: '10px', borderRadius: '6px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '6px',
              }}
            >
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  color: 'var(--text-3)',
                }}
              >
                Estrutura de Subtítulos (H2s Obrigatórios)
              </span>
              <span
                style={{
                  fontSize: '10px',
                  fontWeight: 600,
                  color: 'var(--nexus-brand)',
                  backgroundColor: 'var(--bg-1)',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  border: '1px solid var(--border-2)',
                }}
              >
                Schema: {item.schema_tipo}
              </span>
            </div>
            <ol
              style={{
                margin: 0,
                paddingLeft: '18px',
                fontSize: '12px',
                color: 'var(--text-1)',
                lineHeight: 1.5,
              }}
            >
              {(item.h2s_obrigatorios ?? []).map((h2, idx) => (
                <li key={idx} style={{ marginBottom: '2px' }}>
                  {h2}
                </li>
              ))}
            </ol>
          </div>

          {/* Box 2: Perguntas PAA (People Also Ask) */}
          <div style={{ backgroundColor: 'var(--bg-2)', padding: '10px', borderRadius: '6px' }}>
            <span
              style={{
                fontSize: '11px',
                fontWeight: 700,
                textTransform: 'uppercase',
                color: 'var(--text-3)',
                display: 'block',
                marginBottom: '6px',
              }}
            >
              O que o Google exige responder (PAA Histórico)
            </span>
            <ul
              style={{
                margin: 0,
                paddingLeft: '18px',
                fontSize: '12px',
                color: 'var(--text-2)',
                lineHeight: 1.4,
              }}
            >
              {(item.paa_historico ?? []).map((paa, idx) => (
                <li key={idx} style={{ marginBottom: '2px' }}>
                  {paa}
                </li>
              ))}
            </ul>
          </div>

          {/* Box 3: Resumo 3-Bullets para o Lead (Anti-Clickbait) */}
          <div style={{ backgroundColor: 'var(--bg-2)', padding: '10px', borderRadius: '6px' }}>
            <span
              style={{
                fontSize: '11px',
                fontWeight: 700,
                textTransform: 'uppercase',
                color: 'var(--text-3)',
                display: 'block',
                marginBottom: '6px',
              }}
            >
              Diretriz do Lead (Resumo TL;DR em 3 Bullets)
            </span>
            <ul
              style={{
                margin: 0,
                paddingLeft: '18px',
                fontSize: '12px',
                color: 'var(--text-2)',
                lineHeight: 1.4,
              }}
            >
              {(item.resumo_tldr_3_bullets ?? []).map((bullet, idx) => (
                <li key={idx} style={{ marginBottom: '2px' }}>
                  {bullet}
                </li>
              ))}
            </ul>
          </div>

          {/* Barra de Ações Rápidas do Briefing */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '8px',
            }}
          >
            <button
              onClick={handleCopy}
              style={{
                background: 'var(--bg-1)',
                border: '1px solid var(--border-2)',
                color: 'var(--text-2)',
                fontSize: '11px',
                fontWeight: 600,
                padding: '4px 10px',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <CopyIcon size={12} />
              {copied ? 'Copiado!' : 'Copiar Briefing'}
            </button>

            <a
              href={`/producao?title=${encodeURIComponent(item.titulo)}&category=${encodeURIComponent(item.categoria)}`}
              style={{
                background: 'var(--bg-1)',
                border: '1px solid var(--border-2)',
                color: 'var(--text-1)',
                fontSize: '11px',
                fontWeight: 600,
                padding: '4px 10px',
                borderRadius: '4px',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <EditorArrowIcon size={12} />
              Abrir no Editor
            </a>
          </div>
        </div>
      )}
    </div>
  );
};
